'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useTranslation } from '@/hooks/useTranslation';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { TextField } from '@/components/ui/FormField';
import { Tabs, tabId, tabPanelId } from '@/components/ui/Tabs';
import { authService, AuthState } from '@/lib/auth-direct';
import { isTranslationKey } from '@/lib/translation-utils';
import { VerificationCodeInput } from '@/components/auth/VerificationCodeInput';
import { isLikelyEmail } from '@/lib/validation';

const TABS_ID_PREFIX = 'sign-in-method';

const REDIRECT_PATH = '/dashboard';
const DEFAULT_ERROR_MESSAGE = 'Authentication failed';
const LOGIN_CODE_LENGTH = 6;
type LoginMode = 'password' | 'code';

interface FieldErrors {
  email?: string;
  password?: string;
  code?: string;
}

/**
 * A `callbackUrl` is only safe to redirect to if it is a same-origin relative
 * path. Reject protocol-relative (`//host`), backslash-escaped (`/\host`), and
 * absolute URLs so the parameter cannot be turned into an open-redirect vector.
 */
export function isSafeRelativePath(path: string): boolean {
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//') || path.startsWith('/\\')) return false;
  return true;
}

export function resolveRedirectPath(
  callbackUrl: string | null | undefined
): string {
  if (callbackUrl && isSafeRelativePath(callbackUrl)) {
    return callbackUrl;
  }
  return REDIRECT_PATH;
}

function readCallbackUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('callbackUrl');
}

export function LoginFormDirect() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mode, setMode] = useState<LoginMode>('password');
  const [codeRequested, setCodeRequested] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    tokens: null,
    isLoading: false,
    error: null,
  });
  const { t } = useTranslation();

  useEffect(() => {
    const storedAuth = authService.getStoredAuth();
    if (storedAuth.user && storedAuth.tokens) {
      setAuthState(storedAuth);
      router.push(resolveRedirectPath(readCallbackUrl()));
    }
  }, [router]);

  const handleLoginSuccess = (result: AuthState) => {
    if (result.user && result.tokens) {
      authService.storeAuth(result.user, result.tokens);
      setAuthState(result);
      router.push(resolveRedirectPath(readCallbackUrl()));
    } else {
      setAuthState(result);
    }
  };

  const handleLoginError = (error: unknown) => {
    const errorMessage =
      error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE;
    setAuthState({
      user: null,
      tokens: null,
      isLoading: false,
      error: errorMessage,
    });
  };

  const setError = (errorMessage: string | null) => {
    setAuthState(prev => ({
      ...prev,
      error: errorMessage,
      isLoading: false,
    }));
  };

  /**
   * Names what is wrong per field rather than disabling the button and leaving
   * the user to guess which one it is waiting for.
   */
  const validate = (): FieldErrors => {
    const errors: FieldErrors = {};

    if (!email.trim()) {
      errors.email = t('auth.validation.emailRequired');
    } else if (!isLikelyEmail(email)) {
      errors.email = t('auth.validation.emailInvalid');
    }

    if (mode === 'password') {
      if (!password) {
        errors.password = t('auth.validation.passwordRequired');
      }
    } else if (!codeRequested) {
      errors.code = t('auth.validation.codeNotRequested');
    } else if (code.length !== LOGIN_CODE_LENGTH) {
      errors.code = t('auth.validation.codeIncomplete', {
        length: LOGIN_CODE_LENGTH,
      });
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsLoading(true);
    setAuthState(prev => ({ ...prev, error: null, isLoading: true }));

    try {
      const result =
        mode === 'code'
          ? await authService.loginWithCode(email, code)
          : await authService.login(email, password);
      handleLoginSuccess(result);
    } catch (error) {
      handleLoginError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestCode = async () => {
    if (!email.trim() || !isLikelyEmail(email)) {
      setFieldErrors({
        email: email.trim()
          ? t('auth.validation.emailInvalid')
          : t('auth.validation.emailRequired'),
      });
      return;
    }

    setIsRequestingCode(true);
    setFieldErrors({});
    setCode('');
    setError(null);

    try {
      await authService.requestLoginCode(email);
      setCodeRequested(true);
    } catch (error) {
      handleLoginError(error);
      setCodeRequested(false);
    } finally {
      setIsRequestingCode(false);
    }
  };

  const handleModeChange = (nextMode: LoginMode) => {
    setMode(nextMode);
    setError(null);
    setFieldErrors({});
    setCode('');
    setCodeRequested(false);
  };

  const handleLogout = async () => {
    await authService.logout();
    setAuthState({
      user: null,
      tokens: null,
      isLoading: false,
      error: null,
    });
  };

  const getDisplayName = () => {
    if (!authState.user) return '';
    return authState.user.name || authState.user.email;
  };

  const getErrorMessage = (errorMessage: string) => {
    const genericError = t('auth.signIn.genericError', {
      defaultValue: DEFAULT_ERROR_MESSAGE,
    });

    if (errorMessage === 'auth.signIn.codeRequestFailed') {
      return t('auth.signIn.codeRequestFailed');
    }

    // Never echo an untranslated key back at the user — show the generic message.
    return t(errorMessage, {
      defaultValue: isTranslationKey(errorMessage)
        ? genericError
        : errorMessage || genericError,
    });
  };

  if (authState.user) {
    return (
      <div className="text-center">
        <h2 className="text-xl font-bold text-text-primary">
          {t('auth.welcomeMessage', { name: getDisplayName() })}
        </h2>
        <p className="mt-2 text-sm text-text-secondary">
          {t('auth.successfullyLoggedIn')}
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <ButtonLink href={REDIRECT_PATH} variant="accent">
            {t('auth.goToDashboard')}
          </ButtonLink>
          <Button onClick={handleLogout} variant="ghost">
            {t('auth.logout')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit} noValidate>
      {authState.error && (
        <Alert variant="danger">{getErrorMessage(authState.error)}</Alert>
      )}

      <Tabs
        tabs={[
          { key: 'password', label: t('auth.signIn.passwordTab') },
          { key: 'code', label: t('auth.signIn.codeTab') },
        ]}
        activeKey={mode}
        onChange={handleModeChange}
        ariaLabel={t('auth.signIn.methodLabel')}
        variant="segmented"
        idPrefix={TABS_ID_PREFIX}
      />

      <div className="space-y-4">
        <TextField
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          label={t('auth.emailAddress')}
          placeholder={t('auth.enterYourEmail')}
          value={email}
          onChange={setEmail}
          error={fieldErrors.email}
        />

        {mode === 'password' ? (
          <div
            id={tabPanelId(TABS_ID_PREFIX, 'password')}
            role="tabpanel"
            aria-labelledby={tabId(TABS_ID_PREFIX, 'password')}
          >
            <TextField
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              label={t('auth.password')}
              placeholder={t('auth.enterYourPassword')}
              value={password}
              onChange={setPassword}
              error={fieldErrors.password}
            />
          </div>
        ) : (
          <div
            id={tabPanelId(TABS_ID_PREFIX, 'code')}
            role="tabpanel"
            aria-labelledby={tabId(TABS_ID_PREFIX, 'code')}
            className="space-y-4"
          >
            <p className="text-sm text-text-secondary">
              {t('auth.signIn.codeDescription')}
            </p>
            <Button
              type="button"
              variant="default"
              className="w-full"
              onClick={handleRequestCode}
              loading={isRequestingCode}
            >
              {isRequestingCode
                ? t('auth.signIn.requestingCode')
                : codeRequested
                  ? t('auth.signIn.requestAnotherCodeButton')
                  : t('auth.signIn.requestCodeButton')}
            </Button>

            {codeRequested && (
              <div className="space-y-3" role="status" aria-live="polite">
                <p className="text-sm text-success">
                  {t('auth.signIn.codeSent', { email })}
                </p>
                <div>
                  <label
                    htmlFor="login-code"
                    className="mb-2 block text-sm font-medium text-text-primary"
                  >
                    {t('auth.signIn.codeLabel')}
                  </label>
                  <VerificationCodeInput
                    id="login-code"
                    value={code}
                    onChange={setCode}
                    length={LOGIN_CODE_LENGTH}
                    disabled={isLoading}
                    error={fieldErrors.code}
                  />
                </div>
              </div>
            )}

            {!codeRequested && fieldErrors.code && (
              <p role="alert" className="text-sm text-danger">
                {fieldErrors.code}
              </p>
            )}
          </div>
        )}
      </div>

      <div>
        <Button
          type="submit"
          loading={isLoading}
          className="w-full"
          variant="accent"
        >
          {isLoading
            ? t('auth.signingIn')
            : mode === 'code'
              ? t('auth.signIn.signInWithCodeButton')
              : t('auth.signIn.button')}
        </Button>
      </div>

      <div className="text-center">
        <Link href="/auth/forgot-password" className="text-sm link">
          {t('auth.forgotPassword')}
        </Link>
      </div>
    </form>
  );
}
